import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'

import { AiDesignerActions } from '@flowx/designer/modules/ai/agents/designer/store/designer.actions'
import { AgentName } from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { ChatMessage, DataModelDraft } from '@flowx/designer/modules/ai/types/ai.types'

import { dataModelDraftToRows, rowsToDataModelDraft } from './adapters/designer-dm-rows.adapter'
import { AiDmKeyRow } from './ai-dm-key-row.model'

export type AiDmSummaryView = {
  rows: AiDmKeyRow[]
}

@Injectable({ providedIn: 'root' })
export class AiDmSummaryService {
  #store = inject(Store)

  #designerDraft = this.#store.selectSignal(ConversationSelectors.aiGeneratedUiDataModelChanges)
  #catalogue = this.#store.selectSignal(ConversationSelectors.projectDataModelCatalogueFromContext)
  #uiOutcomeFinished = this.#store.selectSignal(ConversationSelectors.isUiOutcomeFinished)

  buildSummary(message: ChatMessage, isActiveOutcome: boolean): AiDmSummaryView | null {
    switch (message?.agent) {
      case AgentName.AiDeveloper:
        return this.#developerSummary(message)
      case AgentName.AiDesigner:
        return this.#designerSummary(isActiveOutcome)
      default:
        return null
    }
  }

  /** Map edited rows back to the draft and persist. Only designer summaries are editable. */
  applyRowsUpdate(message: ChatMessage, rows: AiDmKeyRow[]): void {
    if (message?.agent !== AgentName.AiDesigner) {
      return
    }
    this.#store.dispatch(
      AiDesignerActions.updateDataModelOnLastMessage({ dataModel: rowsToDataModelDraft(rows) })
    )
  }

  #developerSummary(message: ChatMessage): AiDmSummaryView | null {
    const draft = message.developerDataModelSummary
    return this.#isEmpty(draft)
      ? null
      : { rows: dataModelDraftToRows(draft, this.#catalogue(), false) }
  }

  #designerSummary(isActiveOutcome: boolean): AiDmSummaryView | null {
    if (!isActiveOutcome || !this.#uiOutcomeFinished()) {
      return null
    }
    const draft = this.#designerDraft()
    return this.#isEmpty(draft) ? null : { rows: dataModelDraftToRows(draft, this.#catalogue()) }
  }

  #isEmpty(draft: DataModelDraft | null | undefined): boolean {
    return !draft?.new?.length && !draft?.existing?.length && !draft?.referenced?.length
  }
}
